import type { CellContext, ColumnDef, Row } from '@tanstack/react-table'

import { HighlightText } from '@/components/ui'
import { cn, numberToCurrency } from '@/utils'
import { DuplicateBadge } from '../../duplicates/DuplicateBadge'
import { ReconciliationBadge } from '../../ReconciliationBadge'
import { VerifyingBadge } from '../../VerifyingBadge'
import type { CycleAccountRow } from '../cycleRows'

// biome-ignore lint/suspicious/noExplicitAny: the table's own column type, matching every other table.
type CycleColumn = ColumnDef<CycleAccountRow, any>
type CycleCell = CellContext<CycleAccountRow, unknown>

const searchQuery = (cell: CycleCell) => cell.table.options.meta?.searchQuery

const Dash = () => (
  <span aria-hidden="true" className="select-none text-muted-foreground">
    —
  </span>
)

const statusCell = (cell: CycleCell) =>
  cell.row.original.isVerifying ? (
    <VerifyingBadge className="cursor-pointer" />
  ) : (
    <ReconciliationBadge
      status={cell.row.original.status}
      className="cursor-pointer"
    />
  )

const accountCell = (cell: CycleCell) => {
  const { account, accountType } = cell.row.original

  return (
    <span className="truncate" title={account}>
      <span className="font-medium text-foreground">
        <HighlightText text={account} query={searchQuery(cell)} />
      </span>
      {accountType && (
        <span className="ml-1.5 text-xs text-muted-foreground">
          <HighlightText text={accountType} query={searchQuery(cell)} />
        </span>
      )}
    </span>
  )
}

const periodCell = (cell: CycleCell) => {
  const { period, isDuplicate } = cell.row.original

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">
        <HighlightText text={period} query={searchQuery(cell)} />
      </span>
      {isDuplicate && <DuplicateBadge />}
    </span>
  )
}

const balanceCell = (cell: CycleCell) => {
  const balance = cell.getValue() as number | undefined
  if (balance == null) return <Dash />

  return (
    <span
      className={cn(
        'block truncate tabular-nums',
        balance < 0 && 'text-red-600',
      )}
    >
      {numberToCurrency(balance)}
    </span>
  )
}

const gapCell = (cell: CycleCell) => {
  const { gap } = cell.row.original
  if (gap == null) return <Dash />

  return (
    <span className="block truncate font-semibold tabular-nums text-amber-700">
      {gap > 0 ? '+' : '-'}
      {numberToCurrency(Math.abs(gap))}
    </span>
  )
}

const statementCell = (cell: CycleCell) => {
  const { statement } = cell.row.original

  return (
    <span title={statement} className="block truncate text-muted-foreground">
      <HighlightText text={statement} query={searchQuery(cell)} />
    </span>
  )
}

// Worst verdict first, the engine's own order, rather than the alphabet the words would give.
const byReviewRank = (
  first: Row<CycleAccountRow>,
  second: Row<CycleAccountRow>,
) => first.original.reviewRank - second.original.reviewRank

// By the day the cycle opened, not the words in the cell. A cycle that never got a real date has no
// place on the calendar, so it sorts to the far end.
const byPeriodStart = (
  first: Row<CycleAccountRow>,
  second: Row<CycleAccountRow>,
) => {
  const start = first.original.periodStart
  const other = second.original.periodStart
  if (!start || !other) return Number(!start) - Number(!other)
  return start.localeCompare(other)
}

const statusColumn: CycleColumn = {
  id: 'status',
  header: 'Status',
  size: 140,
  // The verdict in words, so a search for "needs review" finds these rows.
  accessorFn: (row) => row.statusLabel,
  sortingFn: byReviewRank,
  cell: statusCell,
}

const accountColumn: CycleColumn = {
  accessorKey: 'account',
  header: 'Account',
  size: 210,
  cell: accountCell,
}

// The account type reads inside the Account cell. It stays a column of its own, with no header so
// the table hides it, only so search reaches it.
const accountTypeColumn: CycleColumn = { accessorKey: 'accountType' }

const periodColumn: CycleColumn = {
  accessorKey: 'period',
  header: 'Period',
  size: 200,
  sortingFn: byPeriodStart,
  cell: periodCell,
}

// A row with no balance has nothing to order by, so it sits at the bottom whichever way the column
// is pointed. Money is not worth searching: a "1200" typed in the box means a file or an account.
const balanceColumn = (key: 'begin' | 'end', header: string): CycleColumn => ({
  id: key,
  header,
  size: 130,
  accessorFn: (row) => row[key] ?? undefined,
  sortUndefined: 'last',
  enableGlobalFilter: false,
  cell: balanceCell,
})

const gapColumn: CycleColumn = {
  id: 'gap',
  header: 'Off by',
  size: 130,
  // Sorted by how far off it is, so the worst misses group whichever way the miss points.
  accessorFn: (row) => (row.gap == null ? undefined : Math.abs(row.gap)),
  sortUndefined: 'last',
  enableGlobalFilter: false,
  cell: gapCell,
}

const statementColumn: CycleColumn = {
  accessorKey: 'statement',
  header: 'Statement',
  size: 280,
  meta: { isFluid: true },
  cell: statementCell,
}

const ENTITLED: CycleColumn[] = [
  periodColumn,
  statusColumn,
  accountColumn,
  accountTypeColumn,
  balanceColumn('begin', 'Begin'),
  balanceColumn('end', 'End'),
  gapColumn,
  statementColumn,
]

// Without the reconciliation feature the server sends no verdict, gap or printed balance, so those
// columns would print stored zeros as real money. The cycles, accounts and dates still stand.
const UNENTITLED: CycleColumn[] = [
  periodColumn,
  accountColumn,
  accountTypeColumn,
  statementColumn,
]

// One list per entitlement, built once: the table rebuilds its own state whenever the columns change
// identity.
export const cycleColumns = (isReconciliationEntitled: boolean) =>
  isReconciliationEntitled ? ENTITLED : UNENTITLED
